import React, { useState } from 'react';
import { Button, Form, Input, InputNumber } from 'antd';
import { PaymentAccount } from '~/types/payment';
import {
  createPaymentAccount,
  updatePaymentAccount,
} from '~/App/PaymentAccount/api';
import message from '~/utils/message';
import { PaymentAccountFormValues } from '~/App/PaymentAccount/types';
import { usePaymentAccounts } from '~/App/PaymentAccount/hooks/usePaymentAccounts';

const rules = [
  {
    required: true,
    message: 'Please input this field',
  },
];

const PaymentAccountForm = ({
  initialValue,
  onFinish,
}: {
  initialValue?: PaymentAccount;
  onFinish: () => void;
}) => {
  const isEdit = Boolean(initialValue);
  const items = usePaymentAccounts();
  const [loading, setLoading] = useState(false);

  const onSubmit = async (values: PaymentAccountFormValues) => {
    setLoading(true);
    try {
      const api = isEdit
        ? (values: PaymentAccountFormValues) =>
            updatePaymentAccount(initialValue!.paymentAccountId, values)
        : createPaymentAccount;

      await api(values);
      onFinish();
      message.success(
        isEdit
          ? 'Success update payment account'
          : 'Success create payment account'
      );
    } catch (_) {
      message.error(isEdit ? 'Fail to update' : 'Fail to create');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form initialValues={initialValue} onFinish={onSubmit} layout="vertical">
      <Form.Item
        name="accountCode"
        label="Account Code"
        rules={[
          ...rules,
          {
            validator: async (_, value) => {
              if (isEdit) return Promise.resolve();
              if (
                value &&
                items.some(({ accountCode }) => value === accountCode)
              ) {
                return Promise.reject(new Error(`'${value}' is already used`));
              }
              return Promise.resolve();
            },
          },
        ]}
      >
        <Input
          placeholder="Account code"
          readOnly={Boolean(initialValue)}
          disabled={Boolean(initialValue)}
        />
      </Form.Item>

      <Form.Item name="accountName" label="Account Name" rules={rules}>
        <Input placeholder="Account Name" />
      </Form.Item>

      <Form.Item name="accountNumber" label="Account No." rules={rules}>
        <Input placeholder="Account No." />
      </Form.Item>

      <Form.Item name="rotationCount" label="Rotation Count" rules={rules}>
        <InputNumber placeholder="Rotation Count" min={0} className="w-full" />
      </Form.Item>

      <Form.Item name="sequence" label="Order" rules={rules}>
        <InputNumber placeholder="Order" className="w-full" />
      </Form.Item>

      <Form.Item className="pt-6">
        <Button type="primary" htmlType="submit" loading={loading}>
          Submit
        </Button>
      </Form.Item>
    </Form>
  );
};

export default PaymentAccountForm;
